import { Footer, TenantHeader, TopBar } from '@/components/organisms';
import AppSpin from '@/components/atoms/AppSpin';
import { ConsoleTemplate } from '@/components/templates/ConsoleTemplate';
import { isTenantPathAvailableInService } from '@/constants/routes';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { TenantDashboard } from '@/pages/main/dashboard/TenantDashboard';
import { NotFound } from '@/pages/main/system/NotFound';
import Sidebar, { persistSidebarOpen, readPersistedSidebarOpen } from '@/shared/layouts/Sidebar';
import { ModuleLoader } from '@runtime/ModuleLoader';
import {
  getTenantCustomization,
  getTenantCustomizations,
  tenantCapability,
} from '@customize/registry';
import { useSiteType } from '@shared/providers/siteType';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, RouteObject, useLocation, useParams } from 'react-router-dom';

import { ClinicDesignTemplatePage } from '@/pages/design-template';
import ContactEditPage from '@/pages/main/contact/ContactEditPage';
import ContactCompletePage from '@/pages/main/contact/ContactCompletePage';
import PatientCertificationPage from '@/pages/main/patient-certification';
import DistanceChangePage from '@/pages/main/visitSchedule/DistanceChangePage';
import VisitClinicSchedulePage from '@/pages/main/visitClinic/VisitClinicSchedulePage';
import VisitClinicEditPage from '@/pages/main/visitClinic/VisitClinicEditPage';
import { ClinicInformationPage } from '@pages/main/clinic-information';
import { ClinicLocationDetailPage, ClinicLocationPage } from '@pages/main/clinicLocation';
import { CreateConsentPage } from '@pages/main/consent/pages/CreateConsentPage';
import { EditConsentPage } from '@pages/main/consent/pages/EditConsentPage';
import { TreatmentRecordConsentPrintPage } from '@pages/main/consent/pages/TreatmentRecordConsentPrintPage';
import { ConsentTreatmentPeriodListPage } from '@pages/main/consent/pages/ConsentTreatmentPeriodListPage';
import { FreeMedicalPage } from '@pages/main/freeMedical';
import { HospitalPage } from '@pages/main/hospital';
// import { InsurerDetailPage, InsurerSearchPage } from '@pages/main/insurer';
import { InsurerAttentionPage } from '@pages/main/insurerAttention';
import { LongTermFrequentListPage } from '@pages/main/longTermFrequent/pages/LongTermFrequentListPage';
import { LongTermFrequentCreatePage } from '@pages/main/longTermFrequent/pages/LongTermFrequentCreatePage';
import LongTermFrequentEditPage from '@pages/main/longTermFrequent/pages/LongTermFrequentEditPage';
import { MedicalExpensePricePage } from '@pages/main/medicalExpensePrices';
import { OrderSheetPage } from '@pages/main/orderSheet';
import {
  PatientCheckinMailsPage,
  PatientCheckinMailCreatePage,
  PatientCheckinMailEditPage,
  PatientContinuationAddPage,
  PatientContinuationEditPage,
  PatientContinuationPage,
  PatientEditPage,
  PatientHistoriesPage,
} from '@pages/main/patient';
import {
  PatientAddressPage,
  CreatePatientAddressPage,
  EditPatientAddressPage,
} from '@pages/main/patientAddresses';
import { RezeptApplicationCountListPage } from '@pages/main/rezeptApplicationCount/pages/RezeptApplicationCountListPage';
import { SheetHistoriesPage } from '@pages/main/sheetHistories';
import { ConsentListPage } from '@/pages/main/consent/pages/ConsentListPage';
import { TreatmentReportListPage } from '@/pages/main/treatmentReport/pages/TreatmentReportListPage';
import { CreateTreatmentReportPage } from '@/pages/main/treatmentReport/pages/CreateTreatmentReportPage';
import { EditTreatmentReportPage } from '@/pages/main/treatmentReport/pages/EditTreatmentReportPage';
import HomeVisitMassagerPage from '@/pages/main/homeVisitMassager/HomeVisitMassagerPage';
import VisitPatientPage from '@/pages/main/visitPatient/VisitPatientPage';
import SearchPatientListPage from '@/pages/main/searchPatientList/SearchPatientListPage';
import VisitClinicInvoicesPage from '@/pages/main/rezepts/pages/VisitClinicInvoicesPage';
import ReturnInitPage from '@/pages/main/rezepts/pages/ReturnInitPage';
import ClinicNotificationCenterPage from '@/pages/main/notificationCenter/ClinicNotificationCenterPage';
import TreatmentRecordListPage from '@/pages/main/treatmentRecord/pages/TreatmentRecordListPage';
import RezeptInvoiceListPage from '@/pages/main/rezeptsInvoice/RezeptInvoiceListPage';
import RezeptHistoriesListPage from '@/pages/main/rezeptHistories/pages/RezeptHistoriesListPage';
import MassagerCsvPage from '@/pages/main/massagerCsv/MassagerCsvPage';
import RezeptListPage from '@/pages/main/rezepts/pages/RezeptListPage';
import VisitCheckResultPage from '@/pages/main/rezepts/pages/VisitCheckResultPage';
import StatisticalReportPage from '@/pages/main/rezepts/statistical/StatisticalReportPage';

const TenantServiceRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { tenant } = useParams<{ tenant: string }>();
  const { serviceType } = useTheme();
  const siteType = useSiteType();
  const partitionsRoutes = tenantCapability(
    siteType,
    'partitionClinicRoutesByServiceType',
    true
  );

  if (
    partitionsRoutes &&
    (serviceType === SERVICE_TYPE_A2 || serviceType === SERVICE_TYPE_R2) &&
    !isTenantPathAvailableInService(location.pathname, serviceType)
  ) {
    return <Navigate to={tenant ? `/${tenant}` : '/'} replace />;
  }

  return <>{children}</>;
};

/**
 * Tenant Router Layout Component
 * Wraps all tenant routes with the console template, sidebar, header, and footer
 */
const DefaultTenantShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(readPersistedSidebarOpen);
  useEffect(() => {
    persistSidebarOpen(sidebarOpen);
  }, [sidebarOpen]);

  return (
    <ConsoleTemplate
      sidebarOpen={sidebarOpen}
      header={<TenantHeader onMenuClick={() => setSidebarOpen(!sidebarOpen)} />}
      topBar={<TopBar sidebarOpen={sidebarOpen} />}
      sidebar={<Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
      footer={<Footer />}
    >
      {children}
    </ConsoleTemplate>
  );
};

export const TenantRouterLayout: React.FC = () => {
  const siteType = useSiteType();
  useTheme(); // Initialize theme context
  const shellLoader = getTenantCustomization(siteType)?.clinicShell;
  const CustomShell = useMemo(
    () => (shellLoader ? lazy(shellLoader) : null),
    [shellLoader]
  );

  const content = (
    <>
      <TenantServiceRouteGuard>
        <Outlet />
      </TenantServiceRouteGuard>
    </>
  );

  if (!CustomShell) return <DefaultTenantShell>{content}</DefaultTenantShell>;

  return (
    <Suspense fallback={<AppSpin fullscreen size="large" />}>
      <CustomShell>{content}</CustomShell>
    </Suspense>
  );
};

/**
 * Renders a tenant's own screen only under that tenant's site type.
 *
 * Tenant routes are merged into one shared route table because the router tree
 * is built before the URL is known, so scoping has to happen at render time.
 */
const TenantScopedRoute: React.FC<{ code: string; children: React.ReactNode }> = ({
  code,
  children,
}) => {
  const siteType = useSiteType();

  if (siteType !== code) return <NotFound />;

  return <>{children}</>;
};

const TenantIndexPage: React.FC = () => {
  const siteType = useSiteType();
  const indexLoader = getTenantCustomization(siteType)?.clinicIndex;
  const IndexPage = useMemo(
    () => (indexLoader ? lazy(indexLoader) : null),
    [indexLoader]
  );
  return IndexPage ? (
    <Suspense fallback={<AppSpin size="large" />}>
      <IndexPage />
    </Suspense>
  ) : <TenantDashboard />;
};

/** Clinic-portal routes contributed by `customize/`, each scoped to its tenant. */
const customizeRoutes: RouteObject[] = getTenantCustomizations().flatMap(tenant =>
  (tenant.clinicRoutes ?? []).map(route => {
    const RouteComponent = lazy(route.component);
    return {
      path: route.path,
      element: (
        <TenantScopedRoute code={tenant.code}>
          <Suspense fallback={<AppSpin size="large" />}>
            <RouteComponent />
          </Suspense>
        </TenantScopedRoute>
      ),
    };
  })
);

/**
 * Tenant Route Configuration
 * Defines all routes available within the tenant portal
 */
export const tenantRoutes: RouteObject[] = [
  {
    index: true,
    element: <TenantIndexPage />,
  },
  {
    path: '',
    element: <TenantIndexPage />,
  },
  {
    path: 'patients/edit',
    element: <PatientEditPage />,
  },
  {
    path: 'patients/search_patients',
    element: <SearchPatientListPage />,
  },
  {
    path: 'patients/copy/:copyFromId',
    element: <PatientEditPage />,
  },
  {
    path: 'patients/edit/:id',
    element: <PatientEditPage />,
  },
  {
    path: 'patients/continuation/add/:patientId/:searchTreatment/:sortRadio',
    element: <PatientContinuationAddPage />,
  },
  {
    path: 'patients/continuation/edit/:patientId/:searchYearmonth',
    element: <PatientContinuationEditPage />,
  },
  {
    path: 'patients/continuation',
    element: <PatientContinuationPage />,
  },
  {
    path: 'patients/:id/histories',
    element: <PatientHistoriesPage />,
  },
  {
    path: 'patients/:id/dminfo/edit',
    element: (
      <ModuleLoader module="patient" component="PatientDmInfoEdit" notFound={<NotFound />} />
    ),
  },
  {
    path: 'patients/certification',
    element: <PatientCertificationPage />,
  },
  {
    path: 'patient_addresses/:id/list',
    element: <PatientAddressPage />,
  },
  {
    path: 'patient_addresses/:id/create',
    element: <CreatePatientAddressPage />,
  },
  {
    path: 'patient_addresses/:id/edit/:addressId',
    element: <EditPatientAddressPage />,
  },
  {
    path: 'long_term_frequents/:id/list',
    element: <LongTermFrequentListPage />,
  },
  {
    path: 'long_term_frequents/:id/edit/:recordId',
    element: <LongTermFrequentEditPage />,
  },
  {
    path: 'long_term_frequents/:id/create',
    element: <LongTermFrequentCreatePage />,
  },
  {
    path: 'long_term_frequents/:id/edit',
    element: <LongTermFrequentCreatePage />,
  },
  {
    path: 'checkin_mails/:id/list',
    element: <PatientCheckinMailsPage />,
  },
  {
    path: 'consents/treatment_period',
    element: <ConsentTreatmentPeriodListPage />,
  },
  {
    path: 'checkin_mails/:id/create',
    element: <PatientCheckinMailCreatePage />,
  },
  {
    path: 'checkin_mails/:id/edit/:mailId',
    element: <PatientCheckinMailEditPage />,
  },
  {
    path: 'consents/:patientId/list',
    element: <ConsentListPage />,
  },
  {
    path: 'consents/:patientId/create',
    element: <CreateConsentPage />,
  },
  {
    path: 'consents/:patientId/continue',
    element: <CreateConsentPage />,
  },
  {
    path: 'consents/:patientId/edit/:consentId',
    element: <EditConsentPage />,
  },
  {
    path: 'consents/:patientId/:consentId/treatment-record-print',
    element: <TreatmentRecordConsentPrintPage />,
  },
  {
    path: 'treatment-reports/:patientId/list',
    element: <TreatmentReportListPage />,
  },
  {
    path: 'treatment-reports/:patientId/create',
    element: <CreateTreatmentReportPage />,
  },
  {
    path: 'treatment-reports/:patientId/continue',
    element: <CreateTreatmentReportPage />,
  },
  {
    path: 'treatment-reports/:patientId/edit/:id',
    element: <EditTreatmentReportPage />,
  },
  {
    path: 'rezept_application_counts/:patientId/list',
    element: <RezeptApplicationCountListPage />,
  },
  {
    path: 'rezept_application_counts/:patientId/edit',
    element: <RezeptApplicationCountListPage />,
  },
  /** 施術録出力 (Treatment Record Output) — clinic-wide search + print modal, A2 + R2 */
  {
    path: 'treatment_records/init',
    element: <TreatmentRecordListPage />,
  },
  /** レセプト内容CSV出力 (Receipt Content List) — clinic search + CSV export via BottomBar (visible when rows exist) */
  {
    path: 'rezept_histories/init',
    element: <RezeptHistoriesListPage />,
  },
  {
    path: 'rezepts/init_massagers',
    element: <MassagerCsvPage />,
  },
  {
    path: 'rezepts',
    element: <div>Rezepts Module (Coming Soon)</div>,
  },
  /** 領収書・請求書 - Invoice / Receipt (entry point + list) */
  {
    path: 'rezepts/invoices_init',
    element: <RezeptInvoiceListPage />,
  },
  /** レセプト発行 - Receipt List (A2 + R2) */
  {
    path: 'rezepts/init',
    element: <RezeptListPage />,
  },
  /** 訪問データチェック結果 - Visit Data Check Result (BD: per legacy URL).
   *  Two routes registered so :visitmonth can be omitted when the user did not
   *  filter by treatment month (React Router v6 has no optional path params). */
  {
    path: 'rezepts/visit_check_ready/:ids/:yearmonth/:visitmonth',
    element: <VisitCheckResultPage />,
  },
  {
    path: 'rezepts/visit_check_ready/:ids/:yearmonth',
    element: <VisitCheckResultPage />,
  },
  /** [来院用]領収書(窓口) - Front Desk Receipt (In-Clinic) */
  {
    path: 'rezepts/visit_clinic_invoices_init',
    element: <VisitClinicInvoicesPage />,
  },
  /** 返戻・問合せレセプト登録 - Returned Receipt Registration */
  {
    path: 'rezepts/return_init',
    element: <ReturnInitPage />,
  },
  /** 通知センター — the one notification screen this portal has. Two tabs: レセプト同期 · 審査システムからの通知.
      It used to sit BESIDE レセプトのお知らせ, a mailbox of 1 row = 1 rezept; that screen was deleted on
      2026-09-08 (👤 owner: 「giờ logic noti đã đủ. xoá code của màn cũ đi」) once its four 送信失敗 sentences
      reached the エラー詳細 CSV and its 返戻 / 返戻取消 rows reached the 審査システムからの通知 tab. */
  {
    path: 'notification_center',
    element: <ClinicNotificationCenterPage />,
  },
  /** 統計一覧表 - Statistical Report (clinic monthly summary) */
  {
    path: 'rezepts/statistical_init',
    element: <StatisticalReportPage />,
  },
  /**
   * 訪問予定 (week calendar), all three shapes of the path. Behind `ModuleLoader` so a tenant can
   * answer them with its own screen, while every other site keeps `VisitSchedulePage`, which stays
   * the registered base.
   */
  {
    path: 'visits/schedule',
    element: <ModuleLoader module="visit" component="VisitSchedule" notFound={<NotFound />} />,
  },
  // Canonical: 3 segments {massagerId}/{clinicLocationId}/{visitdate} — pair (massager, location)
  // identifies the dropdown entry stable across admin edits.
  {
    path: 'visits/schedule/:massagerId/:clinicLocationId/:visitdate',
    element: <ModuleLoader module="visit" component="VisitSchedule" notFound={<NotFound />} />,
  },
  // Legacy 2-segment URL: cross-link from VisitClinicDetailModal or older bookmarks that
  // only carry massager_id (no location). The page picks the first location for that massager.
  {
    path: 'visits/schedule/:massagerId/:visitdate',
    element: <ModuleLoader module="visit" component="VisitSchedule" notFound={<NotFound />} />,
  },
  {
    path: 'visits/distance_change/:visitdate/:massagerId/:clinicLocationId',
    element: <DistanceChangePage />,
  },
  /** 来院 施術入力 (sidebar / patient nav 施術) — A2 + R2 */
  {
    path: 'visit_clinic/schedule',
    element: <VisitClinicSchedulePage />,
  },
  {
    path: 'visit_clinic/edit/:patientId/:yearmonth',
    element: <VisitClinicEditPage />,
  },
  /**
   * 訪問予定表(患者毎). Behind `ModuleLoader` so a tenant can answer this path
   * with its own screen — KEiLOG's SCR-064 利用者予定 — while every other site
   * keeps `HomeVisitPatientPage`, which stays the registered base.
   */
  {
    path: 'visits/patient_init',
    element: (
      <ModuleLoader module="visit" component="HomeVisitPatientList" notFound={<NotFound />} />
    ),
  },
  {
    path: 'visits/massager_init',
    element: <HomeVisitMassagerPage />,
  },
  {
    path: 'visits/visitpatient_init',
    element: <VisitPatientPage />,
  },
  {
    path: 'visits',
    element: <div>Visits Module (Coming Soon)</div>,
  },
  // マスタメンテナンス
  {
    path: 'clinic_informations/init',
    element: <ClinicInformationPage />,
  },
  // {
  //   path: 'insurers/init',
  //   element: <InsurerSearchPage />,
  // },
  // {
  //   path: 'insurers/detail/:id',
  //   element: <InsurerDetailPage />,
  // },
  {
    path: 'insurer_attentions/init',
    element: <InsurerAttentionPage />,
  },
  {
    // Rendered through the registry so a customized site can replace the
    // screen while the default implementation and URL remain unchanged.
    path: 'massagers/init',
    element: (
      <ModuleLoader module="massager" component="MassagerList" notFound={<NotFound />} />
    ),
  },
  {
    path: 'hospitals/init',
    element: <HospitalPage />,
  },
  {
    path: 'document_templates/init',
    element: (
      <ModuleLoader
        module="documentTemplate"
        component="DocumentTemplate"
        notFound={<NotFound />}
      />
    ),
  },
  {
    path: 'clinic_locations/init',
    element: <ClinicLocationPage />,
  },
  {
    path: 'clinic_locations/:id',
    element: <ClinicLocationDetailPage />,
  },
  {
    path: 'free_medical/init',
    element: <FreeMedicalPage />,
  },
  {
    path: 'medical_expense_prices/init',
    element: <MedicalExpensePricePage />,
  },
  // レセプト用紙発注
  {
    path: 'sheets/order',
    element: <OrderSheetPage />,
  },
  {
    path: 'design-template',
    element: <ClinicDesignTemplatePage />,
  },
  {
    path: 'contact/edit',
    element: <ContactEditPage />,
  },
  {
    path: 'contact/complete',
    element: <ContactCompletePage />,
  },
  {
    path: 'sheets/histories',
    element: <SheetHistoriesPage />,
  },
  ...customizeRoutes,
  {
    path: '*',
    element: <NotFound />,
  },
];
